import React from 'react';
import { Award, Users, Target, TrendingUp, Shield, Heart, Zap, Globe } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
// import components
import Header from '../components/Header';
import Footer from '../components/Footer';

const AboutUs = () => {

    // hooks
    const { isAuth } = useSelector((state) => state.auth);

    const values = [
        {
            icon: Shield,
            title: 'Transparency',
            description: 'We believe in clear, honest communication and transparent business practices at every level.'
        },
        {
            icon: Heart,
            title: 'Integrity',
            description: 'Building trust through ethical practices and keeping our promises to every member.'
        },
        {
            icon: Users,
            title: 'Community',
            description: 'Creating a supportive network where everyone grows together and succeeds.'
        },
        {
            icon: Zap,
            title: 'Innovation',
            description: 'Constantly evolving our platform to provide the best tools and opportunities.'
        }
    ];

    const achievements = [
        { number: '10,000+', label: 'Active Members' },
        { number: '₹5Cr+', label: 'Paid to Members' },
        { number: '50+', label: 'Cities Covered' },
        { number: '98%', label: 'Satisfaction Rate' }
    ];

    const timeline = [
        {
            year: '2020',
            title: 'Foundation',
            description: 'Gateway Promoters was established with a vision to democratize financial growth opportunities.'
        },
        {
            year: '2021',
            title: 'Growth Phase',
            description: 'Expanded to 25+ cities with 5,000+ active promoters in our network.'
        },
        {
            year: '2022',
            title: 'Technology Upgrade',
            description: 'Launched our modern digital platform with enhanced security and user experience.'
        },
        {
            year: '2023',
            title: 'Major Milestone',
            description: 'Crossed ₹5 Crores in total payouts to our network of successful promoters.'
        },
        {
            year: '2024',
            title: 'Continued Excellence',
            description: 'Expanded to 50+ cities with 10,000+ members and introduced new reward tiers.'
        }
    ];

    return (
        <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900">
            <div className="relative overflow-hidden">
                <div className="absolute inset-0 opacity-10">
                    <div className="absolute top-20 right-20 w-96 h-96 bg-cyan-500 rounded-full filter blur-3xl"></div>
                    <div className="absolute bottom-20 left-20 w-96 h-96 bg-blue-500 rounded-full filter blur-3xl"></div>
                </div>
                <Header />
                <div className="relative z-10 px-6 py-20">
                    <div className="max-w-7xl mx-auto text-center">
                        <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
                            About <span className="text-cyan-400">Gateway Promoters</span>
                        </h1>
                        <p className="text-xl text-gray-300 max-w-3xl mx-auto">
                            Empowering individuals to build sustainable income through our innovative binary plan system since 2020
                        </p>
                    </div>
                </div>
            </div>

            <div className="px-6 py-20">
                <div className="max-w-7xl mx-auto">
                    <div className="grid md:grid-cols-2 gap-12 items-center">
                        <div>
                            <h2 className="text-4xl font-bold text-white mb-6">
                                Our <span className="text-cyan-400">Mission</span>
                            </h2>
                            <p className="text-gray-300 text-lg mb-6">
                                At Gateway Promoters, we are committed to creating opportunities for financial independence through a transparent and sustainable binary plan system. Our mission is to empower individuals from all backgrounds to achieve their financial goals while building meaningful connections.
                            </p>
                            <p className="text-gray-300 text-lg mb-6">
                                We believe that success is not just about individual achievement, but about lifting the entire community. Through our innovative compensation plan, comprehensive training, and unwavering support, we help our members transform their aspirations into reality.
                            </p>
                            <div className="flex items-center gap-4 mb-4">
                                <Target className="w-8 h-8 text-cyan-400" />
                                <span className="text-gray-300">Building a community of successful entrepreneurs</span>
                            </div>
                            <div className="flex items-center gap-4 mb-4">
                                <Globe className="w-8 h-8 text-cyan-400" />
                                <span className="text-gray-300">Expanding opportunities across India</span>
                            </div>
                            <div className="flex items-center gap-4">
                                <Award className="w-8 h-8 text-cyan-400" />
                                <span className="text-gray-300">Recognized for ethical business practices</span>
                            </div>
                        </div>

                        <div className="bg-gradient-to-br from-cyan-500/20 to-blue-600/20 rounded-2xl p-8 border border-cyan-500/30">
                            <div className="space-y-6">
                                <div className="bg-slate-800/50 rounded-xl p-6 border border-cyan-500/20">
                                    <div className="flex items-center gap-4 mb-4">
                                        <div className="w-12 h-12 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-lg flex items-center justify-center">
                                            <TrendingUp className="w-6 h-6 text-white" />
                                        </div>
                                        <h3 className="text-xl font-bold text-white">Vision</h3>
                                    </div>
                                    <p className="text-gray-300">
                                        To be India's most trusted and preferred network marketing platform, creating wealth opportunities for millions.
                                    </p>
                                </div>

                                <div className="bg-slate-800/50 rounded-xl p-6 border border-cyan-500/20">
                                    <div className="flex items-center gap-4 mb-4">
                                        <div className="w-12 h-12 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-lg flex items-center justify-center">
                                            <Users className="w-6 h-6 text-white" />
                                        </div>
                                        <h3 className="text-xl font-bold text-white">Philosophy</h3>
                                    </div>
                                    <p className="text-gray-300">
                                        Success is achieved through teamwork, dedication, and a commitment to ethical business practices.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="px-6 py-20 bg-slate-800/50">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-16">
                        <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
                            Our <span className="text-cyan-400">Core Values</span>
                        </h2>
                        <p className="text-xl text-gray-400">The principles that guide everything we do</p>
                    </div>

                    <div className="grid md:grid-cols-4 gap-8">
                        {values.map((value, index) => {
                            const Icon = value.icon;
                            return (
                                <div
                                    key={index}
                                    className="bg-slate-800/50 backdrop-blur-sm rounded-xl p-6 border border-cyan-500/20 hover:border-cyan-500/50 transition-all hover:transform hover:scale-105 text-center"
                                >
                                    <div className="w-16 h-16 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-lg flex items-center justify-center mx-auto mb-4">
                                        <Icon className="w-8 h-8 text-white" />
                                    </div>
                                    <h3 className="text-xl font-bold text-white mb-3">{value.title}</h3>
                                    <p className="text-gray-400">{value.description}</p>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>

            <div className="px-6 py-20">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-16">
                        <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
                            Our <span className="text-cyan-400">Achievements</span>
                        </h2>
                        <p className="text-xl text-gray-400">Numbers that speak for themselves</p>
                    </div>

                    <div className="grid md:grid-cols-4 gap-8">
                        {achievements.map((achievement, index) => (
                            <div
                                key={index}
                                className="bg-gradient-to-br from-cyan-500/20 to-blue-600/20 rounded-2xl p-8 border border-cyan-500/30 text-center hover:transform hover:scale-105 transition-all"
                            >
                                <h3 className="text-4xl md:text-5xl font-bold text-cyan-400 mb-3">
                                    {achievement.number}
                                </h3>
                                <p className="text-xl text-white font-semibold">{achievement.label}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            <div className="px-6 py-20 bg-slate-800/50">
                <div className="max-w-5xl mx-auto">
                    <div className="text-center mb-16">
                        <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
                            Our <span className="text-cyan-400">Journey</span>
                        </h2>
                        <p className="text-xl text-gray-400">Building success, one milestone at a time</p>
                    </div>

                    <div className="space-y-8">
                        {timeline.map((item, index) => (
                            <div
                                key={index}
                                className="relative bg-slate-800/50 backdrop-blur-sm rounded-xl p-8 border border-cyan-500/20 hover:border-cyan-500/50 transition-all"
                            >
                                <div className="flex items-start gap-6">
                                    <div className="flex-shrink-0">
                                        <div className="w-20 h-20 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-lg flex items-center justify-center">
                                            <span className="text-2xl font-bold text-white">{item.year}</span>
                                        </div>
                                    </div>
                                    <div className="flex-1">
                                        <h3 className="text-2xl font-bold text-white mb-3">{item.title}</h3>
                                        <p className="text-gray-300 text-lg">{item.description}</p>
                                    </div>
                                </div>
                                {index < timeline.length - 1 && (
                                    <div className="absolute left-10 top-28 w-0.5 h-8 bg-cyan-500/30"></div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            <div className="px-6 py-20">
                <div className="max-w-4xl mx-auto">
                    <div className="bg-gradient-to-r from-cyan-500 to-blue-600 rounded-3xl p-12 text-center">
                        <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
                            Join Our Success Story
                        </h2>
                        <p className="text-xl text-white/90 mb-8">
                            Be part of a growing community that's transforming lives through opportunity and innovation
                        </p>
                        <div className="flex flex-wrap justify-center gap-4">
                            {
                                isAuth ? (
                                    <Link to="/dashboard" className="px-8 py-4 bg-white text-cyan-600 rounded-lg font-semibold hover:bg-gray-100 transition-all transform hover:scale-105 shadow-xl">
                                       Get Started
                                    </Link>

                                ) : (
                                    <Link to="/register" className="px-8 py-4 bg-white text-cyan-600 rounded-lg font-semibold hover:bg-gray-100 transition-all transform hover:scale-105 shadow-xl">
                                        Register
                                    </Link>
                                )
                            }
                            <Link to="/contact-us" className="px-8 py-4 border-2 border-white text-white rounded-lg font-semibold hover:bg-white hover:text-cyan-600 transition-all">
                                Contact Us
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
            <Footer />
        </div>
    );
};

export default AboutUs;